import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAuthStore } from "../Store/auth";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiMail, FiLock, FiEye, FiEyeOff, FiWifiOff } from "react-icons/fi";
import axios from "axios";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { API_BASE } from "../config/api";

export default function Login() {
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [offline, setOffline] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const abortRef = useRef(null);

  // Remember the email only; passwords belong in the browser password manager.
  const [savedEmail, setSavedEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    try {
      const email = localStorage.getItem("savedEmail") || "";
      localStorage.removeItem("savedPassword");
      const remembered = localStorage.getItem("rememberMe") === "true";

      setSavedEmail(email);
      setRememberMe(remembered && !!email);
    } catch {
      // Local storage may be unavailable in a restricted browser.
      setSavedEmail("");
      setRememberMe(false);
    }
  }, []);

  const initialValues = useMemo(
    () => ({
      email: savedEmail,
      password: "",
      rememberMe: rememberMe,
    }),
    [savedEmail, rememberMe]
  );

  const validationSchema = Yup.object({
    email: Yup.string()
      .trim()
      .lowercase("Email should be lowercase")
      .email("Invalid email")
      .required("Required"),
    password: Yup.string().min(6, "Minimum 6 characters").required("Required"),
    rememberMe: Yup.boolean(),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting, setFieldError }) => {
    setLoading(true);
    setOffline(false);

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    try {
      const res = await axios.post(
        `${API_BASE}/auth/login`,
        {
          email: values.email.trim().toLowerCase(),
          password: values.password,
        },
        { signal: abortRef.current.signal, timeout: 12000 }
      );

      const token = res?.data?.token;
      const coachId = res?.data?.coachId;

      if (!token || !coachId) {
        toast.error("Invalid response from server.");
        return;
      }

      // persist auth
      login(token, coachId);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("token", token);
      localStorage.setItem("coachId", coachId);

      // Remove legacy password storage and remember only the email.
      localStorage.removeItem("savedPassword");
      if (values.rememberMe) {
        localStorage.setItem("savedEmail", values.email.trim().toLowerCase());
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("savedEmail");
        localStorage.removeItem("savedPassword");
        localStorage.setItem("rememberMe", "false");
      }

      toast.success("Logged in successfully!");
      resetForm();
      const redirectTo = location.state?.from || "/dashboard/profile";
      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (axios.isCancel(error)) return;

      if (error.code === "ECONNABORTED") {
        setOffline(true);
        toast.error("Request timed out. Please check your connection and try again.");
      } else if (error.message === "Network Error") {
        setOffline(true);
        toast.error("Network error. Are you offline?");
      } else {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.response?.data ||
          "Invalid email or password";
        setFieldError("password", msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-100 via-sky-100 to-white p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl p-8 w-full max-w-md space-y-6"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100, damping: 14 }}
      >
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-blue-800">Welcome back</h1>
          <p className="text-slate-600 mt-1">Sign in to continue</p>
        </div>

        {offline && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-800">
            <FiWifiOff />
            <span>We couldn’t reach the server. Check your internet and try again.</span>
          </div>
        )}

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
        >
          {({ isSubmitting, values, setFieldValue, isValid /*, dirty*/ }) => (
            <Form className="space-y-5" autoComplete="on">
              {/* Email */}
              <div className="relative">
                <FiMail className="absolute top-3 left-3 text-gray-400" />
                <Field
                  id="email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoComplete="username"
                  placeholder="Email address"
                  className="pl-10 py-3 w-full rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm ml-1 mt-1" />
              </div>

              {/* Password + toggle */}
              <div className="relative">
                <FiLock className="absolute top-3 left-3 text-gray-400" />
                <Field
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Password"
                  className="pl-10 pr-10 py-3 w-full rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
                <ErrorMessage name="password" component="div" className="text-red-500 text-sm ml-1 mt-1" />
              </div>

              {/* Remember + links */}
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <Field
                    type="checkbox"
                    name="rememberMe"
                    checked={values.rememberMe}
                    onChange={(e) => setFieldValue("rememberMe", e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-800">Remember my email</span>
                </label>

                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-700 hover:text-blue-800 underline underline-offset-2"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit */}
              <motion.button
                type="submit"
                className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition flex items-center justify-center space-x-2 ${
                  loading || isSubmitting || !isValid ? "opacity-70 cursor-not-allowed" : ""
                }`}
                whileTap={{ scale: 0.98 }}
                disabled={loading || isSubmitting || !isValid}
              >
                {loading ? (
                  <motion.div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Login"
                )}
              </motion.button>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* PT signup CTA */}
              <Link
                to="/signup"
                className="block w-full text-center border border-blue-200 text-blue-700 hover:bg-blue-50 font-semibold py-3 rounded-xl transition"
              >
                Create a PT Account
              </Link>
            </Form>
          )}
        </Formik>
      </motion.div>
    </motion.div>
  );
}
